import { useState } from 'react';
import type { ComponentType, MouseEvent, SVGProps } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useLinkComponent } from '@astryxdesign/core/Link';
import { isAppleTouch, tapFeedback } from './haptics';
import { liquidGlass } from './liquidGlass';
import '@astryxdesign/core/theme/tokens.stylex';
import { colorVars, durationVars, easeVars, fontWeightVars, radiusVars, spacingVars, textSizeVars, typeScaleVars, typographyVars } from '@astryxdesign/core/theme/tokens.stylex';
export interface BottomTabItem {
  key: string;
  label: string;
  href: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  isActive: boolean;
  badge?: number;
  badgeLabel?: string;
}
export interface BottomTabBarProps {
  label: string;
  items: BottomTabItem[];
  onSelect?: (item: BottomTabItem) => void;
}
function appleTouch(): boolean {
  return typeof navigator !== 'undefined' && isAppleTouch({
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints ?? 0
  });
}
const styles = {
  nav: {
    kVAEAm: "xixxii4",
    kkqhue: "x17y0mx6",
    krVfgx: "x1ey2m1c",
    kY2c9j: "xf5e64p",
    kEafiO: "x178xt8z",
    kPef9Z: "x13fuv20",
    kLZC3w: "xrcd9ri",
    kGVxlE: "xidr2az",
    kGO01o: "x11ba12",
    $$css: true
  },
  glass: {
    k1xSpc: "x1lliihq",
    kzqmXN: "xh8yej3",
    kZKoxP: "x5yr21d",
    $$css: true
  }
};
export function BottomTabBar({
  label,
  items,
  onSelect
}: BottomTabBarProps) {
  const LinkComponent = useLinkComponent();
  const [nativeSwitch] = useState(appleTouch);
  const activeIndex = items.findIndex(item => item.isActive);
  const choose = (item: BottomTabItem, event?: MouseEvent) => {
    if (!onSelect) return;
    if (event && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    event?.preventDefault();
    onSelect(item);
  };
  return <nav aria-label={label} {...stylex.props(liquidGlass.surface, styles.nav)} data-qs-bottom-tabs="">
      <ul className="x1n2onr6 xrvj5dj x5j4mdr x3ct3a4 x1ghz6dp x1717udv" style={{
      ['--qs-tab-count' as string]: String(items.length),
      ['--qs-tab-index' as string]: String(Math.max(activeIndex, 0))
    }}>
        <li aria-hidden="true" data-qs-tab-glass="" {...{
        0: {
          className: "x10l6tqk xxk6nc9 xy1j3rs xu96u03 x1mbrs6e xfn1xvj x9f619 x47corl xsfgmze xwcsmn1 x4wkmsb x1szcz9v xr2dlui"
        },
        1: {
          className: "x10l6tqk xxk6nc9 xy1j3rs xu96u03 x1mbrs6e xfn1xvj x9f619 x47corl xsfgmze xwcsmn1 x4wkmsb x1szcz9v xr2dlui xg01cxk"
        }
      }[!!(activeIndex < 0) << 0]}>
          <span {...stylex.props(liquidGlass.pill, styles.glass)} />
        </li>
        {items.map(item => {
        const Icon = item.icon;
        return <li key={item.key} className="x1n2onr6 x1vjfegm">
              <LinkComponent href={item.href} aria-current={item.isActive ? 'page' : undefined} onClick={(event: MouseEvent) => {
            if (!item.isActive) tapFeedback();
            choose(item, event);
          }} {...{
            0: {
              className: "x78zum5 xdt5ytf x6s0dn4 xl56j7k x1lsbc85 x879a55 xce4md1 xkjnx3w xv1l7n4 x1hl2dhg x9ynric x141an7d x1e4wzip x1u7k74 x1i10hfl xr2mjdv xt3l3uh xuedmi6 xlr8y92"
            },
            1: {
              className: "x78zum5 xdt5ytf x6s0dn4 xl56j7k x1lsbc85 x879a55 xce4md1 xkjnx3w x1hl2dhg x9ynric x141an7d x1e4wzip x1u7k74 x1i10hfl xr2mjdv xt3l3uh xuedmi6 xlr8y92 xjse4m1"
            }
          }[!!item.isActive << 0]}>
                <span className="x1n2onr6 x78zum5 x6s0dn4 xl56j7k x15yg21f x1fgtraw">
                  <Icon aria-hidden="true" className="x17z2i9w x17rw0jw" />
                  {item.badge ? <span className="x10l6tqk xjibb99 x1mhylyq x15kz4h8 xlup9mm x9f619 xfn1xvj xn4wktv xjspbzw x1pjz0fi x1m024r3 x51wmvv x2mo6ok x1d3mw78 x2b8uid xss6m8b" aria-label={item.badgeLabel}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span> : null}
                </span>
                <span className="x193iq5w xb3r6kr xlyipyv xuxw1ft">{item.label}</span>
              </LinkComponent>
              {nativeSwitch && onSelect ? <input type="checkbox" {...{
            switch: ''
          }} aria-hidden="true" tabIndex={-1} data-qs-tab-haptic={item.key} className="x10l6tqk x10a8y8t xh8yej3 x5yr21d x1ghz6dp x1717udv xg01cxk x1vjfegm x1ypdohk x1i10hfl" onClick={() => choose(item)} /> : null}
            </li>;
      })}
      </ul>
    </nav>;
}